import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  Box,
  Flex,
  Select,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  SearchIcon,
  TriangleDownIcon,
  TriangleUpIcon,
  DownloadIcon,
} from "@chakra-ui/icons";
import {
  useTable,
  usePagination,
  useFilters,
  useGlobalFilter,
  useSortBy,
  useAsyncDebounce,
} from "react-table";
import { useMemo, useState, useEffect } from "react";
import { matchSorter } from "match-sorter";
import NoData from "./NoData";
import { useUiContext } from "../../contexts/UiContext";
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
}
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <InputGroup background={"white"}>
      <InputLeftElement
        pointerEvents="none"
        children={<SearchIcon color="gray.600" />}
      />
      <Input
        value={value || ""}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        placeholder={`${count} kayıt...`}
        variant="outline"
      />{" "}
    </InputGroup>
  );
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val;

export default function ClientTable({ data, columns, tableName, fileName = "InaExcelExport" }) {
  const filterTypes = useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const { uiState, dispatchUiChange } = useUiContext();

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      filterTypes,
      initialState: uiState[tableName],
    },
    useFilters, // useFilters!
    useGlobalFilter, // useGlobalFilter!
    useSortBy,
    usePagination
  );

  const { pageIndex, pageSize, globalFilter, sortBy } = state;

  useEffect(() => {
    if (tableName) {
      dispatchUiChange({
        type: "tableUIChange",
        payload: { name: tableName, state },
      });
    }
  }, [pageIndex, pageSize, globalFilter, sortBy[0]?.id, sortBy[0]?.desc]);

  const exportToExcel = () => {
    const header = columns.map(col => col.Header);
    const wsData = [header, ...data.map(row => columns.map(col => row[col.accessor]))];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(dataBlob, `${fileName}.xlsx`); // Dosya adını fileName prop'undan al
  };

  if (data.length === 0) return <NoData />;
  return (
    <>
      <Box mt={2} display="flex" alignItems="center" gap={2}>
        <GlobalFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
        <IconButton onClick={exportToExcel} colorScheme="pink"  icon={<DownloadIcon />}/>
        
      </Box>
      <Box mt="2">
        <Box maxWidth={"100%"} overflowX={"auto"} overflowY={"hidden"}>
          <Table {...getTableProps()}>
            <Thead>
              {headerGroups.map((headerGroup) => (
                <Tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      isNumeric={column.isNumeric}
                    >
                      {column.render("Header")}
                      <chakra.span pl="4">
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <TriangleDownIcon aria-label="sorted descending" />
                          ) : (
                            <TriangleUpIcon aria-label="sorted ascending" />
                          )
                        ) : null}
                      </chakra.span>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <Tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <Td
                        fontSize={"sm"}
                        {...cell.getCellProps()}
                        isNumeric={cell.column.isNumeric}
                      >
                        {cell.render("Cell")}
                      </Td>
                    ))}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>

        <Box px="4" py="2" fontSize={"sm"}>
          <Flex justify={"space-between"} alignItems="center">
            <Flex>
              <Select
                size="sm"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
              >
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </Select>
            </Flex>
            <Flex alignItems="center">
              <Box>
                <Text>
                  {pageIndex + 1} - {pageOptions.length}
                </Text>{" "}
              </Box>
              <Box>
                <Button
                  colorScheme="red"
                  variant={"ghost"}
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                >
                  <ChevronLeftIcon />
                </Button>
                <Button
                  colorScheme="red"
                  variant={"ghost"}
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                >
                  <ChevronRightIcon />
                </Button>
              </Box>
            </Flex>
          </Flex>
        </Box>
      </Box>
    </>
  );
}
